import type { PieceKind, SpecialType, WorldTheme } from '../core/types';

export interface PiecePalette {
  primary: number;
  secondary: number;
  emissive: number;
  accent: number;
  roughness: number;
  metalness: number;
  /** Pattern id for colorblind differentiation */
  pattern: 'stripes' | 'dots' | 'rings' | 'chevrons' | 'cross' | 'waves' | 'diamonds';
}

export const PIECE_PALETTES: Record<PieceKind, PiecePalette> = {
  ember: {
    primary: 0xff6b3d,
    secondary: 0xffc14a,
    emissive: 0xff3d1a,
    accent: 0xffe0a0,
    roughness: 0.35,
    metalness: 0.15,
    pattern: 'chevrons',
  },
  tide: {
    primary: 0x2ec4b6,
    secondary: 0x7fdbda,
    emissive: 0x0a8f9e,
    accent: 0xd4fffc,
    roughness: 0.25,
    metalness: 0.35,
    pattern: 'waves',
  },
  gale: {
    primary: 0x7dd3fc,
    secondary: 0xe0f2fe,
    emissive: 0x38bdf8,
    accent: 0xffffff,
    roughness: 0.4,
    metalness: 0.05,
    pattern: 'stripes',
  },
  root: {
    primary: 0x5a9a4a,
    secondary: 0xa3d977,
    emissive: 0x2d5a27,
    accent: 0xd4e8a8,
    roughness: 0.55,
    metalness: 0.08,
    pattern: 'rings',
  },
  solar: {
    primary: 0xfbbf24,
    secondary: 0xfde68a,
    emissive: 0xf59e0b,
    accent: 0xfff7d6,
    roughness: 0.3,
    metalness: 0.45,
    pattern: 'diamonds',
  },
  moon: {
    primary: 0xc4b5fd,
    secondary: 0xe9d5ff,
    emissive: 0x8b5cf6,
    accent: 0xf5f3ff,
    roughness: 0.28,
    metalness: 0.4,
    pattern: 'dots',
  },
  void: {
    primary: 0x6d28d9,
    secondary: 0xa78bfa,
    emissive: 0x4c1d95,
    accent: 0xddd6fe,
    roughness: 0.2,
    metalness: 0.6,
    pattern: 'cross',
  },
};

export const SPECIAL_COLORS: Record<SpecialType, number> = {
  beam: 0x67e8f9,
  nova: 0xfcd34d,
  prism: 0xf0abfc,
  burst: 0xfb7185,
};

export const WORLD_THEMES: Record<
  WorldTheme,
  {
    bgTop: number;
    bgBottom: number;
    fog: number;
    ambient: number;
    key: number;
    rim: number;
    board: number;
    frame: number;
  }
> = {
  ember: {
    bgTop: 0x1a0a08,
    bgBottom: 0x3d1810,
    fog: 0x2a100c,
    ambient: 0xffb090,
    key: 0xff8a5c,
    rim: 0xffd0a0,
    board: 0x3a2218,
    frame: 0xc45c26,
  },
  tide: {
    bgTop: 0x061820,
    bgBottom: 0x0a3040,
    fog: 0x0a2830,
    ambient: 0x90e0e8,
    key: 0x40c4d4,
    rim: 0xb0fff8,
    board: 0x143038,
    frame: 0x2a9d8f,
  },
  gale: {
    bgTop: 0x0a1528,
    bgBottom: 0x152848,
    fog: 0x102038,
    ambient: 0xb0d0ff,
    key: 0x80b8ff,
    rim: 0xe0f0ff,
    board: 0x1a2840,
    frame: 0x6090d0,
  },
  root: {
    bgTop: 0x0a180c,
    bgBottom: 0x1a3020,
    fog: 0x102418,
    ambient: 0xa0d090,
    key: 0x70b050,
    rim: 0xd0f0a0,
    board: 0x1e3020,
    frame: 0x5a8a40,
  },
  void: {
    bgTop: 0x08061a,
    bgBottom: 0x140a30,
    fog: 0x0c0820,
    ambient: 0xc0a0ff,
    key: 0x9060e0,
    rim: 0xe0d0ff,
    board: 0x181028,
    frame: 0x7040c0,
  },
};

export const PIECE_LABELS: Record<PieceKind, string> = {
  ember: 'Ember Petal',
  tide: 'Tide Coil',
  gale: 'Gale Plume',
  root: 'Root Knot',
  solar: 'Solar Crest',
  moon: 'Moon Chime',
  void: 'Void Prism',
};

export const SPECIAL_LABELS: Record<SpecialType, string> = {
  beam: 'Beam Spindle',
  nova: 'Nova Loom',
  prism: 'Prism Heart',
  burst: 'Burst Cocoon',
};
